import React from 'react'
import Compressor from 'compressorjs'
import {
  deleteRoom,
  leaveRoom,
  patchRoomSettings,
  setRoomArchived,
} from '../../../../api/chat'
import { convertBase64 } from '../../../../api/helpers'
import { createMedia } from '../../../../api/media'
import { RoomHeader } from '../../../../generated/api/chat-room'
import { PatchRoomBody } from '../../../../generated/api/chat-room-settings'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useHamburger } from '../../../../hooks/useHamburgerContext'
import { useModal } from '../../../../hooks/useModal'
import { theme } from '../../../../theme/theme'
import { Button } from '../../../common/Button'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { Divider } from '../../../common/Divider'
import { IconChoices } from '../../../common/Icon'
import {
  InformationRowOptions,
  InformationTable,
} from '../../../common/InformationTable'
import { Toggle } from '../../../common/Toggle'
import { chipFullName } from '../ChipAvatar'
import { RoomAvatarField } from './RoomAvatarField'
import { RoomMembersModal } from './RoomMembersModal'
import { RoomNameField } from './RoomNameField'
import { AddMembersModal } from './AddMembersModal'
import { useRoomExitMutation } from './useExitRoom'
import { useRoomHeaderMutation } from './useRoomHeaderMutation'

const compressImage = (file: File): Promise<File | Blob> =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line no-new
    new Compressor(file, { quality: 0.8, success: resolve, error: reject })
  })

interface RoomSettingsPanelProps {
  header: RoomHeader
}

export const RoomSettingsPanel = ({
  header,
}: RoomSettingsPanelProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { isHamburgerMode } = useHamburger()
  const roomId = header.id
  const { canEditRoom, canManageMembers, canDelete, canLeave, canArchive } =
    header.capabilities
  const isInterests = header.type === 'interests'

  const archiveModal = useModal()
  const deleteModal = useModal()
  const leaveModal = useModal()
  const membersModal = useModal()
  const addMembersModal = useModal()

  const { mutate: patch, isPending: isPatching } = useRoomHeaderMutation(
    (body: PatchRoomBody) => patchRoomSettings(roomId, body),
    {
      successMessage: translate('flashMessage.changesSaved'),
      errorMessage: translate('flashMessage.changesNotSavedError'),
    }
  )

  const { mutate: updateAvatar, isPending: isUpdatingAvatar } =
    useRoomHeaderMutation(
      async (file: File | null) => {
        if (file === null) {
          return patchRoomSettings(roomId, { avatarMediaId: null })
        }
        const base64 = await convertBase64(await compressImage(file))
        const mediaId = (await createMedia('image', base64 as string)).data.data
          ?._id
        if (mediaId === undefined) throw new Error("Couldn't create media")
        return patchRoomSettings(roomId, { avatarMediaId: mediaId as string })
      },
      { errorMessage: translate('flashMessage.somethingWentWrongError') }
    )

  const archive = useRoomExitMutation({
    roomId,
    mutationFn: () => setRoomArchived(roomId, true),
    successMessage: translate(
      'chatSettings.flashMessage.archiveChatRoomSuccess'
    ),
    errorMessage: translate('chatSettings.flashMessage.archiveChatRoomFailure'),
  })
  const leave = useRoomExitMutation({
    roomId,
    mutationFn: () => leaveRoom(roomId),
    successMessage: translate('chatSettings.flashMessage.leaveChatRoomSuccess'),
    errorMessage: translate('chatSettings.flashMessage.leaveChatRoomFailure'),
  })
  const remove = useRoomExitMutation({
    roomId,
    mutationFn: () => deleteRoom(roomId),
    successMessage: translate(
      'chatSettings.flashMessage.deleteChatRoomSuccess'
    ),
    errorMessage: translate('chatSettings.flashMessage.deleteChatRoomFailure'),
  })

  const creatorName = chipFullName(header.createdBy)
  const informationRows: InformationRowOptions[] = [
    ...(creatorName
      ? [
          {
            title: translate('chatSettings.form.chatCreator'),
            text: creatorName,
          },
        ]
      : []),
    { title: translate('chatSettings.form.society'), text: header.societyName },
    ...(isInterests && !canEditRoom
      ? [
          {
            title: translate('chatSettings.form.type'),
            text: translate(
              header.isPrivate
                ? 'chatSettings.form.closedGroupChat'
                : 'chatSettings.form.openGroupChat'
            ),
          },
        ]
      : []),
    ...(isInterests
      ? [
          {
            title: `${translate('chatSettings.form.showMembers')} (${
              header.memberCount
            })`,
            icon: IconChoices.GROUPS,
            onClick: membersModal.open,
            testId: 'room-settings-members',
          },
        ]
      : []),
  ]

  const dangerRow = (
    title: string,
    icon: IconChoices,
    onClick: () => void,
    testId: string
  ): InformationRowOptions => ({
    title,
    icon,
    iconColor: theme.colors.danger,
    titleStyle: { color: theme.colors.danger },
    onClick,
    hideElementRight: true,
    testId,
  })

  const actionRows: InformationRowOptions[] = [
    ...(canArchive
      ? [
          {
            title: translate('chatSettings.form.archive'),
            icon: IconChoices.INVENTORY,
            onClick: archiveModal.open,
            hideElementRight: true,
            testId: 'room-settings-archive',
          },
        ]
      : []),
    ...(canLeave
      ? [
          dangerRow(
            translate('chatSettings.form.leave'),
            IconChoices.LOGOUT,
            leaveModal.open,
            'room-settings-leave'
          ),
        ]
      : []),
    ...(canDelete
      ? [
          dangerRow(
            translate('chatSettings.form.remove'),
            IconChoices.DELETE_TRASH,
            deleteModal.open,
            'room-settings-delete'
          ),
        ]
      : []),
  ]

  return (
    <div
      className={
        isHamburgerMode
          ? 'h-56 w-full flex-none overflow-y-scroll pb-2'
          : 'w-sidebar'
      }
      data-testid="room-settings-panel"
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-6 px-6 pt-8">
          {isInterests && (
            <p style={theme.textVariants.lead}>
              {translate('chatSettings.tables.titles.groupChatInfo')}
            </p>
          )}
          <div className="flex flex-row items-center gap-5">
            <RoomAvatarField
              header={header}
              canEdit={canEditRoom}
              isSaving={isUpdatingAvatar}
              onPickImage={updateAvatar}
              onRemoveImage={() => updateAvatar(null)}
            />
            <RoomNameField
              name={header.title}
              canEdit={canEditRoom}
              isSaving={isPatching}
              onSave={(name) => patch({ name })}
            />
          </div>
          {canManageMembers && (
            <Button
              type="button"
              label={translate('chatSettings.tables.titles.addMembers')}
              wrapperClassName="w-full"
              className="h-11 w-full"
              size="no-padding"
              iconSize={18}
              icon={IconChoices.PERSON_ADD}
              onClick={addMembersModal.open}
              data-testid="room-settings-add-members"
            />
          )}
        </div>
        <div className="mx-3 flex flex-col">
          <InformationTable className="px-3" rows={informationRows} />
          {isInterests && canEditRoom && (
            <div
              className="flex w-full items-center justify-between rounded px-3 py-3"
              data-testid="room-settings-private"
            >
              <div className="flex flex-col">
                <p style={theme.textVariants.baseBold}>
                  {translate('chatSettings.form.type')}
                </p>
                <p className="text-neutral-40">
                  {translate(
                    header.isPrivate
                      ? 'chatSettings.form.closedGroupChat'
                      : 'chatSettings.form.openGroupChat'
                  )}
                </p>
              </div>
              <Toggle
                name="room-settings-private-toggle"
                enabled={header.isPrivate}
                loading={isPatching}
                onChange={(isPrivate) => patch({ isPrivate })}
              />
            </div>
          )}
        </div>
        {actionRows.length > 0 && (
          <>
            <Divider className="mx-6" />
            <div className="mx-3 mt-3 flex flex-col">
              <p className="px-3 py-1" style={theme.textVariants.lead}>
                {translate('chatSettings.tables.titles.actions')}
              </p>
              <InformationTable className="px-3" rows={actionRows} />
            </div>
          </>
        )}
      </div>
      <RoomMembersModal
        show={membersModal.show}
        close={membersModal.close}
        header={header}
      />
      {canManageMembers && (
        <AddMembersModal
          show={addMembersModal.show}
          close={addMembersModal.close}
          roomId={roomId}
        />
      )}
      <ConfirmationModal
        title={translate('chatSettings.alert.archiveChatRoomTitle')}
        description={translate('chatSettings.alert.archiveChatRoomText')}
        show={archiveModal.show}
        close={archiveModal.close}
        onConfirm={archive}
        confirmationButtonLabel={translate('chatSettings.form.archive')}
      />
      <ConfirmationModal
        title={translate('chatSettings.alert.chatRoomDeleteTitle')}
        description={translate('chatSettings.alert.chatRoomDeleteText')}
        show={deleteModal.show}
        close={deleteModal.close}
        onConfirm={remove}
      />
      <ConfirmationModal
        title={translate('chatSettings.alert.leaveChatRoomTitle')}
        description={translate('chatSettings.alert.leaveChatRoomText')}
        show={leaveModal.show}
        close={leaveModal.close}
        onConfirm={leave}
        confirmationButtonLabel={translate('common.actions.leave')}
      />
    </div>
  )
}
